/* hs-eslint ignored failing-rules */

'use es6';

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { debounce, isEqual } from 'underscore';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBadge from 'UIComponents/badge/UIBadge';
import { FormsLangToLang } from 'ContentData/utils/forms';
import ExpandableSection from 'ContentComponents/cmv2/components/ExpandableSection';
import FormResponse from 'ContentComponents/cmv2/form/FormResponse';
import { ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import FormSaveIndicator from 'ContentComponents/cmv2/form/FormSaveIndicatorContainer';
import FormAutomation from 'ContentComponents/cmv2/form/formAutomation/FormAutomation';
import FormFieldEditor from './FormFieldEditor';
import FormContent from './formContent/FormContent';
import FormSelectorSelect from './FormSelectorSelect';
import CreateFormButton from './CreateFormButton';
import PagesModalContainer from './PagesModalContainer';
import PagesSidePanelContainer from './PagesSidePanelContainer';
import CreateFormWithNameModal from './CreateFormWithNameModal';
import MarketingContactsWarning from './MarketingContactsWarning';
import { NoFormsWriteScopeMessage } from 'ContentComponents/components/cmv2/fields/Form';
import getLegalConsentCopy from 'forms-utils-lib/api/legalConsent/getLegalConsentCopy';
import buildLegalConsent from 'forms-utils-lib/legalConsent/buildLegalConsent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const inlineEditingI18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.${key}`;
const i18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.createForm.withNameModal.${key}`;

// Uses ExpandableSection with cmv2-field-group classes to keep in line with grouping patterns
const FormExpandableSection = props => /*#__PURE__*/_jsx(ExpandableSection, Object.assign({
  withContentPadding: true,
  defaultOpen: true
}, props, {
  className: "cmv2-field-group without-nested-groups"
}));
let willScrollAfterRemount = false;
class FormWithInlineEditing extends PureComponent {
  constructor(props) {
    super(props);
    const {
      FormDefinition,
      loadOptions,
      selectedForm
    } = props;
    this.onWebinarChange = this.onWebinarChange.bind(this);
    this.onFormAutomationOpenChange = this.onFormAutomationOpenChange.bind(this);
    this.onFormContentOpenChange = this.onFormContentOpenChange.bind(this);
    this.onThankYouOpenChange = this.onThankYouOpenChange.bind(this);
    this.onShowPagesStateChange = this.onShowPagesStateChange.bind(this);
    this.showFormFollowupEmail = this.showFormFollowupEmail.bind(this);
    this.flushQueuedFormDefinition = this.flushQueuedFormDefinition.bind(this);
    this.showFormCreateModal = this.showFormCreateModal.bind(this);
    this.hideFormCreateModal = this.hideFormCreateModal.bind(this);
    this.setFormCreateConflictError = this.setFormCreateConflictError.bind(this);
    this.handleCreateForm = this.handleCreateForm.bind(this);
    this.onEditField = this.onEditField.bind(this);
    this.handleCloneForm = this.handleCloneForm.bind(this);
    this.toggleFormCloneModal = this.toggleFormCloneModal.bind(this);
    this.toggleClonePendingStatus = this.toggleClonePendingStatus.bind(this);
    this.handleGdprValueChange = this.handleGdprValueChange.bind(this);
    this.scrollToFollowupEmail = this.scrollToFollowupEmail.bind(this);
    this.debouncedLoadOptions = debounce(loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      pagesSidePanelOpen: false,
      isShowingFormCreateModal: false,
      isCreateFormConflictError: false,
      selectedFieldId: null,
      formAutomationOpen: false,
      formContentOpen: false,
      thankYouOpen: false,
      hasIntendedToEdit: false,
      isShowingFormCloneModal: false,
      isClonePending: false,
      currentGuid: null,
      formDefinition: FormDefinition && selectedForm ? new FormDefinition(selectedForm) : undefined
    };
  }
  componentDidMount() {
    const {
      fetchDraftForm,
      fetchProperties,
      fetchSubscriptions,
      fetchDefaultConsentCopy,
      fetchConsentCopyFn,
      fetchParentPages
    } = this.props;
    if (this.formGuid) {
      fetchDraftForm(this.formGuid);
      fetchParentPages(this.formGuid);
      fetchProperties();
      fetchSubscriptions();
      if (fetchConsentCopyFn) {
        fetchDefaultConsentCopy(fetchConsentCopyFn);
      }
    }
    document.addEventListener('FIELD_REVEAL', this.showFormFollowupEmail);
    if (willScrollAfterRemount) {
      document.dispatchEvent(new CustomEvent('FIELD_REVEAL_LISTENER_READY'));
      willScrollAfterRemount = false;
    }
  }
  componentDidUpdate(prevProps) {
    const {
      FormDefinition
    } = this.props;

    // If this field was previously focused but is now no longer focused,
    // we are exiting the form field editing experience
    if (isEqual(prevProps.currentFieldKey, this.props.fieldKey) && !isEqual(this.props.currentFieldKey, this.props.fieldKey)) {
      this.setState({
        selectedFieldId: null,
        formContentOpen: true
      });
    }
    if (!isEqual(prevProps.selectedForm, this.props.selectedForm)) {
      const {
        guid: previousFormGuid
      } = prevProps.selectedForm || {};
      const {
        guid: currentFormGuid
      } = this.props.selectedForm || {};
      this.setState(Object.assign({
        formDefinition: new FormDefinition(this.props.selectedForm)
      }, previousFormGuid !== currentFormGuid ? {
        formContentOpen: true
      } : {}));
    }
  }
  componentWillUnmount() {
    document.removeEventListener('FIELD_REVEAL', this.showFormFollowupEmail);
  }
  get fieldKey() {
    const {
      fieldKey
    } = this.props;
    return fieldKey && fieldKey.join('.');
  }
  get formGuid() {
    const {
      value
    } = this.props;
    if (!value) return null;
    if (typeof value === 'object') {
      return value.form_id || null;
    }
    return value;
  }
  scrollToFollowupEmail(formFieldPath, afterRemount = false) {
    const {
      scrollToField
    } = this.props;
    if (afterRemount) {
      willScrollAfterRemount = true;
    }
    if (scrollToField && formFieldPath) {
      scrollToField({
        fieldKey: formFieldPath.split('.'),
        section: 'form-followup-email',
        dispatchEvent: true,
        afterRemount
      });
    }
  }
  showFormFollowupEmail({
    detail: {
      fieldKey,
      section
    }
  }) {
    if (isEqual(fieldKey, this.props.fieldKey) && section === 'form-followup-email') {
      const scrollIntoView = () => {
        const el = document.querySelector(`.cmv2-edit-form [data-field-key="${fieldKey.join('.')}"] [data-field-section="form-followup-email"]`);
        if (el) {
          el.scrollIntoViewIfNeeded();
        }
      };
      this.setState({
        formContentOpen: false,
        thankYouOpen: false
      });
      if (!this.state.formAutomationOpen) {
        this.setState({
          formAutomationOpen: true
        });
        window.setTimeout(scrollIntoView, 250);
      } else {
        scrollIntoView();
      }
    }
  }
  showFormCreateModal() {
    this.setState({
      isShowingFormCreateModal: true,
      isCreateFormConflictError: false
    });
  }
  hideFormCreateModal() {
    this.setState({
      isShowingFormCreateModal: false,
      isCreateFormConflictError: false
    });
  }
  setFormCreateConflictError() {
    this.setState({
      isCreateFormConflictError: true
    });
  }
  toggleFormCloneModal(guid = null) {
    this.setState({
      isShowingFormCloneModal: !this.state.isShowingFormCloneModal,
      currentGuid: guid
    });
  }
  getShouldPromptPagesModal() {
    const {
      shouldShowPagesModal
    } = this.props;
    const {
      hasIntendedToEdit
    } = this.state;
    return hasIntendedToEdit && shouldShowPagesModal;
  }
  onWebinarChange(webinarKey) {
    this.props.onFieldChange(Object.assign({}, this.props.value, {
      gotowebinar_webinar_key: webinarKey
    }));
  }
  onFormAutomationOpenChange(e) {
    this.setState({
      formAutomationOpen: e.target.value
    });
  }
  onFormContentOpenChange(e) {
    this.setState({
      formContentOpen: e.target.value
    });
  }
  onThankYouOpenChange(e) {
    this.setState({
      thankYouOpen: e.target.value
    });
  }
  onShowPagesStateChange() {
    this.setState({
      pagesSidePanelOpen: !this.state.pagesSidePanelOpen
    });
  }
  onEditField(selectedFieldId) {
    this.props.setFieldKey(this.props.fieldKey);
    this.props.onOpenEditFieldPanelEvent();
    this.setState({
      hasIntendedToEdit: true,
      selectedFieldId
    });
  }
  toggleClonePendingStatus() {
    this.setState({
      isClonePending: !this.state.isClonePending
    });
  }
  handleCreateForm(name) {
    const {
      createFormFn
    } = this.props;
    createFormFn(name, {
      shouldRedirect: false,
      onSuccess: this.hideFormCreateModal,
      onConflictError: this.setFormCreateConflictError
    });
  }
  handleCloneForm(name) {
    const {
      cloneFormFn
    } = this.props;
    this.toggleClonePendingStatus();
    cloneFormFn(this.state.currentGuid, name).then(({
      guid
    }) => this.props.onFormChange({
      target: {
        value: guid
      }
    })).catch(err => console.error(err)).finally(() => {
      this.toggleFormCloneModal();
      this.toggleClonePendingStatus();
    });
  }
  handleGdprValueChange(consentType) {
    const {
      subscriptions,
      onGdprChangeEvent
    } = this.props;
    const {
      formDefinition
    } = this.state;
    const {
      metaData,
      businessUnitId
    } = formDefinition;
    const formLanguage = metaData.find(i => i.name === 'lang').value;
    const language = FormsLangToLang[formLanguage] || formLanguage;
    onGdprChangeEvent();
    getLegalConsentCopy({
      language,
      businessUnitId
    }).then(legalConsentCopy => {
      const updatedLegaConsent = buildLegalConsent({
        legalConsentOption: consentType,
        legalConsentCopy,
        subscriptionDefinitions: subscriptions
      });
      this.updateForm(formDefinition.updateMetaDataValue('legalConsentOptions', updatedLegaConsent));
    }).catch(err => {
      Raven.captureMessage('Failed to update Form GDPR value', err);
    });
  }
  flushQueuedFormDefinition() {
    const {
      queuedFormDefinition
    } = this.state;
    if (queuedFormDefinition) {
      this.setState({
        queuedFormDefinition: null
      });
      this.updateForm(queuedFormDefinition);
    }
  }
  updateForm(formDefinition) {
    const {
      shouldShowPagesModal,
      updatePreview,
      hasFormsWriteAccess
    } = this.props;
    const {
      hasIntendedToEdit
    } = this.state;
    if (!hasFormsWriteAccess) {
      return;
    }
    if (shouldShowPagesModal && !hasIntendedToEdit) {
      this.setState({
        hasIntendedToEdit: true,
        queuedFormDefinition: formDefinition
      });
      return;
    }
    const definition = formDefinition.toJS();
    this.props.updateFormDraft(definition, updatePreview);
  }
  maybeRenderPagesModal() {
    const {
      onFormChange
    } = this.props;
    return this.getShouldPromptPagesModal() ? /*#__PURE__*/_jsx(PagesModalContainer, {
      onFormChange: onFormChange,
      formGuid: this.formGuid,
      onClose: this.flushQueuedFormDefinition
    }) : null;
  }
  renderResponse() {
    const {
      componentsByType,
      fieldKey,
      onFieldChange,
      readOnly,
      useOnDark,
      value
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-5",
      children: [/*#__PURE__*/_jsx(UIAlert, {
        type: "info",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: inlineEditingI18nKey('thankYou.pageOnlyNote')
        })
      }), /*#__PURE__*/_jsx(FormResponse, {
        componentsByType: componentsByType,
        fieldKey: fieldKey,
        onFieldChange: onFieldChange,
        readOnly: readOnly,
        useOnDark: useOnDark,
        value: value,
        hasFormsWriteAccess: true
      })]
    });
  }
  renderSelectedField() {
    const {
      useOnDark,
      openFormFn,
      hasFormsWriteAccess,
      maybeRenderInlineEditingOptIn
    } = this.props;
    const {
      selectedFieldId,
      formDefinition
    } = this.state;
    const field = this.state.formDefinition.getFormFieldById(selectedFieldId);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [hasFormsWriteAccess ? maybeRenderInlineEditingOptIn(field.objectTypeId, field.name) : /*#__PURE__*/_jsx(NoFormsWriteScopeMessage, {}), /*#__PURE__*/_jsxs("div", {
        className: "form-inline-field-editing",
        children: [this.maybeRenderPagesModal(), /*#__PURE__*/_jsx(FormSaveIndicator, {
          form: formDefinition
        }), /*#__PURE__*/_jsx(FormFieldEditor, {
          field: field,
          useOnDark: useOnDark,
          openFormFn: openFormFn,
          onChange: newField => {
            this.updateForm(this.state.formDefinition.updateFormField(newField));
          }
        })]
      })]
    });
  }
  renderAccordions() {
    const {
      value,
      useOnDark,
      hasAutomatedEmailEditorAccess,
      hasEmailMarketingAccess,
      hasInlineEmailEditorAccess,
      hasWorkflowsAccess,
      hasWorkflowsAppAccess,
      hasWorkflowsDeleteAccess,
      hasWorkflowsWriteAccess,
      isGoToWebinarEnabled,
      legacyFollowupEmailId,
      clearLegacyFollowupEmail,
      DraggableComponents,
      consentCopy,
      hasMarketingContactsAccess,
      hasMarketingContactsPermissions,
      allowFormEditingDnd,
      openFormFn,
      onAutomationChange,
      hasFormsWriteAccess,
      onIsCreatingMarketingContactsChange,
      readOnly,
      contentId,
      onAddWorkflowEvent,
      onEditWorkflowEvent,
      onRemoveWorkflowEvent,
      onChangeFollowupEmailEvent,
      onFollowupEmailIdChange,
      followupEmailSelectedForForm
    } = this.props;
    const {
      formDefinition
    } = this.state;
    return /*#__PURE__*/_jsxs("div", {
      className: "forms-inline-editing",
      "data-selenium": `${formDefinition.name}:accordions`,
      children: [/*#__PURE__*/_jsx(FormExpandableSection, {
        "data-selenium": "form-expandable-section-content",
        onOpenChange: this.onFormContentOpenChange,
        open: this.state.formContentOpen,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: inlineEditingI18nKey('formContent.label')
        }),
        children: /*#__PURE__*/_jsx(FormContent, {
          DraggableComponents: DraggableComponents,
          formGuid: this.formGuid,
          useOnDark: useOnDark,
          formDefinition: formDefinition,
          formFields: formDefinition.getFields(),
          buttonText: formDefinition.getSubmitText(),
          isCaptchaEnabled: formDefinition.getIsCaptchaEnabled(),
          canChangeFieldsOrder: allowFormEditingDnd && formDefinition.canUpdateFormFieldsOrder(),
          gdprValue: formDefinition.getGdprConsentType(),
          isGdprSelectDisabled: !consentCopy,
          onButtonTextChange: submitText => this.updateForm(formDefinition.setSubmitText(submitText)),
          onIsCaptchaEnabledChange: captchaValue => this.updateForm(formDefinition.setIsCaptchaEnabled(captchaValue)),
          onRemoveFieldClick: id => {
            this.props.onRemoveFormFieldEvent();
            this.updateForm(formDefinition.removeFormFieldById(id));
          },
          onAddFieldClick: property => {
            this.props.onAddFormFieldEvent();
            this.updateForm(formDefinition.addFormField({
              property
            }));
          },
          onChangeFieldsOrder: updatedOrder => {
            this.props.onReorderFieldsEvent();
            this.updateForm(formDefinition.updateFormFieldsOrder(updatedOrder));
          },
          onEditFieldClick: this.onEditField,
          onGdprValueChange: this.handleGdprValueChange,
          openFormFn: openFormFn,
          hasFormsWriteAccess: hasFormsWriteAccess,
          readOnly: readOnly
        })
      }), /*#__PURE__*/_jsx(FormExpandableSection, {
        "data-selenium": "form-expandable-section-thankyou",
        onOpenChange: this.onThankYouOpenChange,
        open: this.state.thankYouOpen,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: inlineEditingI18nKey('thankYou.label')
        }),
        children: this.renderResponse()
      }), /*#__PURE__*/_jsx(FormExpandableSection, {
        "data-selenium": "form-expandable-section-automation",
        onOpenChange: this.onFormAutomationOpenChange,
        open: this.state.formAutomationOpen,
        titleText: /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: inlineEditingI18nKey('automation.label')
          }), /*#__PURE__*/_jsx(UIBadge, {
            className: "m-left-1",
            use: "new",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: inlineEditingI18nKey('automation.newBadge')
            })
          })]
        }),
        children: /*#__PURE__*/_jsx(FormAutomation, {
          contentId: contentId,
          followupEmailId: value.followup_email_id,
          formGuid: value.form_id,
          formName: formDefinition.name,
          useOnDark: useOnDark,
          goToWebinarValue: value.gotowebinar_webinar_key,
          isGoToWebinarEnabled: isGoToWebinarEnabled,
          legacyFollowupEmailId: legacyFollowupEmailId,
          clearLegacyFollowupEmail: clearLegacyFollowupEmail,
          onGoToWebinarSelectChange: this.onWebinarChange,
          alwaysCreateContactForNewEmail: formDefinition.getAlwaysCreateContactForNewEmail(),
          onAlwaysCreateContactForNewEmailChange: newContactsValue => {
            onAutomationChange();
            this.updateForm(formDefinition.setAlwaysCreateContactForNewEmail(newContactsValue));
          },
          shouldShowMarketingContactsToggle: hasMarketingContactsAccess,
          hasMarketingContactsPermissions: hasMarketingContactsPermissions,
          isCreatingMarketingContacts: formDefinition.getIsCreatingMarketingContacts(),
          onIsCreatingMarketingContactsChange: newValue => {
            onAutomationChange();
            onIsCreatingMarketingContactsChange(newValue);
            this.updateForm(formDefinition.setIsCreatingMarketingContacts(newValue));
          },
          readOnly: readOnly,
          hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
          hasEmailMarketingAccess: hasEmailMarketingAccess,
          hasFormsWriteAccess: hasFormsWriteAccess,
          hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
          hasWorkflowsAccess: hasWorkflowsAccess,
          hasWorkflowsAppAccess: hasWorkflowsAppAccess,
          hasWorkflowsDeleteAccess: hasWorkflowsDeleteAccess,
          hasWorkflowsWriteAccess: hasWorkflowsWriteAccess,
          onAddWorkflowEvent: onAddWorkflowEvent,
          onEditWorkflowEvent: onEditWorkflowEvent,
          onRemoveWorkflowEvent: onRemoveWorkflowEvent,
          onChangeFollowupEmailEvent: onChangeFollowupEmailEvent,
          onFollowupEmailIdChange: onFollowupEmailIdChange,
          scrollToFollowupEmail: this.scrollToFollowupEmail,
          followupEmailSelectedForForm: followupEmailSelectedForForm
        })
      })]
    });
  }
  render() {
    const {
      defaultFormNamePrefix,
      extraDropdownFooterContent,
      onFormChange,
      value,
      rawHtmlEnabled,
      hasFormsWriteAccess,
      hasMarketingContactsAccess
    } = this.props;
    const {
      formDefinition,
      pagesSidePanelOpen,
      isShowingFormCreateModal,
      isCreateFormConflictError,
      isShowingFormCloneModal
    } = this.state;
    if (this.state.selectedFieldId) {
      return this.renderSelectedField();
    }
    const formSelectorSelectProps = Object.assign({
      fieldKey: this.fieldKey,
      rawHtmlEnabled,
      debouncedLoadOptions: this.debouncedLoadOptions
    }, this.props);
    return /*#__PURE__*/_jsxs("div", {
      "data-selenium": "form-with-inline-editing",
      children: [this.maybeRenderPagesModal(), /*#__PURE__*/_jsx(FormSelectorSelect, Object.assign({}, formSelectorSelectProps, {
        formDefinition: formDefinition,
        value: value.form_id,
        onChange: onFormChange,
        onCloneClick: this.toggleFormCloneModal,
        onEditorClick: this.props.openFormFn,
        onCreateClick: this.showFormCreateModal,
        onPagesClick: this.onShowPagesStateChange,
        onViewSubmissionsClick: this.props.openSubmissionsFn,
        hasFormsWriteAccess: hasFormsWriteAccess,
        dropdownFooter: /*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          children: [extraDropdownFooterContent, /*#__PURE__*/_jsx(CreateFormButton, {
            className: "m-top-0",
            use: "link",
            onClick: this.showFormCreateModal,
            hasPermission: hasFormsWriteAccess
          })]
        })
      })), !this.formGuid && /*#__PURE__*/_jsx(CreateFormButton, {
        onClick: this.showFormCreateModal,
        hasPermission: hasFormsWriteAccess
      }), hasMarketingContactsAccess && formDefinition && /*#__PURE__*/_jsx(MarketingContactsWarning, {
        enabled: formDefinition.getIsCreatingMarketingContacts()
      }), formDefinition && this.renderAccordions(), pagesSidePanelOpen && /*#__PURE__*/_jsx(PagesSidePanelContainer, {
        formGuid: this.formGuid,
        onClose: this.onShowPagesStateChange
      }), isShowingFormCreateModal && /*#__PURE__*/_jsx(CreateFormWithNameModal, {
        defaultFormNamePrefix: defaultFormNamePrefix,
        isConflictError: isCreateFormConflictError,
        onCancelClick: this.hideFormCreateModal,
        onCreateClick: this.handleCreateForm,
        headerText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('heading')
        }),
        createText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('create')
        })
      }), isShowingFormCloneModal && /*#__PURE__*/_jsx(CreateFormWithNameModal, {
        defaultFormNamePrefix: defaultFormNamePrefix,
        onCancelClick: this.toggleFormCloneModal,
        onCreateClick: this.handleCloneForm,
        isCreating: this.state.isClonePending,
        headerText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cloneForm.heading')
        }),
        createText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cloneForm.clone')
        })
      })]
    });
  }
}
FormWithInlineEditing.propTypes = {
  componentsByType: PropTypes.object.isRequired,
  contentId: PropTypes.number,
  currentFieldKey: PropTypes.array,
  fieldKey: PropTypes.array,
  isGoToWebinarEnabled: PropTypes.bool,
  legacyFollowupEmailId: PropTypes.number,
  clearLegacyFollowupEmail: PropTypes.func,
  hasAutomatedEmailEditorAccess: PropTypes.bool,
  hasEmailMarketingAccess: PropTypes.bool,
  hasInlineEmailEditorAccess: PropTypes.bool,
  hasWorkflowsAccess: PropTypes.bool,
  hasWorkflowsAppAccess: PropTypes.bool,
  hasWorkflowsDeleteAccess: PropTypes.bool,
  hasWorkflowsWriteAccess: PropTypes.bool,
  followupEmailSelectedForForm: PropTypes.object,
  loadOptions: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFollowupEmailIdChange: PropTypes.func.isRequired,
  onFormChange: PropTypes.func.isRequired,
  rawHtmlEnabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  setFieldKey: PropTypes.func.isRequired,
  useOnDark: PropTypes.bool,
  value: PropTypes.object,
  fetchDraftForm: PropTypes.func.isRequired,
  fetchProperties: PropTypes.func.isRequired,
  fetchSubscriptions: PropTypes.func.isRequired,
  fetchDefaultConsentCopy: PropTypes.func.isRequired,
  updateFormDraft: PropTypes.func.isRequired,
  updatePreview: PropTypes.func.isRequired,
  selectedForm: PropTypes.object,
  FormDefinition: PropTypes.func.isRequired,
  DraggableComponents: PropTypes.object,
  subscriptions: PropTypes.array.isRequired,
  consentCopy: PropTypes.object,
  fetchConsentCopyFn: PropTypes.func,
  openFormFn: PropTypes.func,
  createFormFn: PropTypes.func,
  fetchParentPages: PropTypes.func,
  shouldShowPagesModal: PropTypes.bool.isRequired,
  hasFormsWriteAccess: PropTypes.bool,
  hasMarketingContactsAccess: PropTypes.bool,
  hasMarketingContactsPermissions: PropTypes.bool,
  onAddFormFieldEvent: PropTypes.func,
  onRemoveFormFieldEvent: PropTypes.func,
  onOpenEditFieldPanelEvent: PropTypes.func,
  onReorderFieldsEvent: PropTypes.func,
  onGdprChangeEvent: PropTypes.func,
  onAutomationChange: PropTypes.func,
  onIsCreatingMarketingContactsChange: PropTypes.func,
  onAddWorkflowEvent: PropTypes.func,
  onEditWorkflowEvent: PropTypes.func,
  onRemoveWorkflowEvent: PropTypes.func,
  onChangeFollowupEmailEvent: PropTypes.func,
  cloneFormFn: PropTypes.func,
  allowFormEditingDnd: PropTypes.bool,
  defaultFormNamePrefix: PropTypes.string,
  openSubmissionsFn: PropTypes.func,
  maybeRenderInlineEditingOptIn: PropTypes.func.isRequired,
  scrollToField: PropTypes.func,
  extraDropdownFooterContent: PropTypes.node
};
FormWithInlineEditing.defaultProps = {
  defaultOptions: [],
  fieldKey: [],
  forms: {},
  options: [],
  pages: {},
  readOnly: false,
  value: {
    response_type: 'inline',
    message: ''
  },
  updatePreview: () => {}
};
export default FormWithInlineEditing;